import { useMemo } from 'react'
import { useFlag } from '@openfeature/react-sdk'
import { DateTime } from 'luxon'
import currency from 'currency.js'
import { faCheck, faTag } from '@fortawesome/pro-solid-svg-icons'
import { selectIsFetchingForKeys } from '@/reducers/fetch'
import { selectIsCurrentUserScorp } from '@/selectors/user.selectors'
import { selectApproximatePracticeStartDate } from '@/selectors/catchupBookkeeping.selectors'

import { useFetchResponse, useReselector } from '@/utils/sharedHooks'
import { DATE_FORMATS_LUXON } from '@/utils/dateHelpers'
import { DeviceWidth, useIsDeviceWidth } from '@/utils/deviceWidthHelpers'
import { formatCurrency } from '@/utils/currencyHelpers'
import {
  FETCH_CATCH_UP_BK_COUPON,
  fetchCatchUpBKMonths,
} from '@/components/CatchUpBookkeepingStatus/catchUpBookkeepingStatus.slice'
import { useCatchupBookkeepingPrice } from './hooks'
import { useLatestCatchUpBkCoupon } from './useLatestCatchUpBkCoupon'
import { CatchUpBkTreatment, discountedCatchUpBkAmount } from './helpers'
import {
  Badge,
  Card,
  CardColors,
  Icon,
  List,
  Loader,
  Skeleton,
  Text,
  Tooltip,
} from '@/ui'
import { TextColors, TypographyStyles } from '@/styles'
import { DetailsAccordion } from './DetailsAccordion'

import {
  selectCanOptIntoPreviousTaxYear,
  selectCurrentAnnualTaxYear,
} from '@/features/admin/annual-tax-details/annualTaxDetails.selector'
import { getAnnualTaxFilingForYearSelector } from '@/features/taxes/annual-taxes/annualTaxFilings.selector'
import { PlanTier } from '@/actions/settings/billingActions'
import { selectProductDescription } from '@/features/user/signup/selectors'
import { FEATURE_FLAG_KEYS } from '@/features/open-feature'
import { UNLIMITED_STATE_FILINGS_COPY } from '@/features/user/signup/constants'
import { ProductFeatureId } from '@/actions/permissionActions'
import {
  formatPayrollPackageLine,
  formatTaxAdvisoryPackageLine,
  resolveBankingBonusLine,
} from './taxPackageCopy'

type CubkDiscount = { percentOff: number; amount: string; savings: string }

const TaxPackageCard = ({
  cubkDiscount,
  payrollLine,
  stateFilingsLine,
  advisoryLine,
}: {
  cubkDiscount: CubkDiscount | null
  payrollLine: string | null
  stateFilingsLine: string | null
  advisoryLine: string | null
}) => (
  <Card color={CardColors.LEVEL_1}>
    <div className="flex">
      <Icon icon={faTag} />
      <Text variant={TypographyStyles.LABEL_MD}>Tax package promotion</Text>
    </div>

    {cubkDiscount ? (
      <div className="flex justify-between mt-3">
        <Text
          color={TextColors.GREEN}
          variant={TypographyStyles.BODY_SM}
          className="font-normal text-neutral-600"
        >
          {cubkDiscount.percentOff}% off catch-up bookkeeping
        </Text>
        <Text
          textAlign="right"
          variant={TypographyStyles.BODY_SM}
          className="font-normal text-neutral-600"
        >
          {cubkDiscount.amount}
        </Text>
      </div>
    ) : null}
    {payrollLine ? (
      <Text
        variant={TypographyStyles.BODY_SM}
        className="font-normal text-neutral-600"
        color={TextColors.GREEN}
      >
        {payrollLine}
      </Text>
    ) : null}
    {stateFilingsLine ? (
      <Text
        variant={TypographyStyles.BODY_SM}
        className="font-normal text-neutral-600"
        color={TextColors.GREEN}
      >
        {stateFilingsLine}
      </Text>
    ) : null}
    {advisoryLine ? (
      <Text
        variant={TypographyStyles.BODY_SM}
        className="font-normal text-neutral-600"
        color={TextColors.GREEN}
      >
        {advisoryLine}
      </Text>
    ) : null}
  </Card>
)

const TaxPackageCardSkeleton = () => (
  <div>
    <Skeleton
      data-testid="tax-package-card-skeleton"
      className="h-48 w-full rounded-lg"
    />
  </div>
)

const TaxSeasonPackageCard = ({
  cubkDiscount,
  payrollLine,
  stateFilingsLine,
  advisoryLine,
  bankingLine,
  freeTrialEnabled,
}: {
  cubkDiscount: CubkDiscount | null
  payrollLine: string | null
  stateFilingsLine: string | null
  advisoryLine: string | null
  bankingLine: string | null
  freeTrialEnabled?: boolean
}) => {
  const intro = freeTrialEnabled
    ? "When you become a paid member, you'll receive:"
    : "When you join today, you'll receive:"
  const items = [
    cubkDiscount
      ? `${cubkDiscount.percentOff}% off catch-up bookkeeping (save ${cubkDiscount.savings})`
      : null,
    payrollLine,
    stateFilingsLine,
    advisoryLine,
    bankingLine,
  ].filter((item): item is string => Boolean(item))

  if (items.length === 0) {
    return null
  }

  return (
    <Card color={CardColors.LEVEL_1}>
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <Icon icon={faTag} />
          <Text variant={TypographyStyles.LABEL_MD}>
            Get ready for tax season
          </Text>
        </div>
        <Text variant={TypographyStyles.BODY_SM} color={TextColors.MUTED}>
          {intro}
        </Text>
        <List className="flex flex-col gap-3 px-0">
          {items.map((item) => (
            <List.Item key={item} className="mb-0 flex items-start text-left">
              <Icon
                icon={faCheck}
                color="text-green-50"
                className="mr-1 mt-0.5 inline-block"
              />
              <Text variant={TypographyStyles.LABEL_SM}>{item}</Text>
            </List.Item>
          ))}
        </List>
      </div>
    </Card>
  )
}

export const CatchupBookkeepingSection = ({
  selectedAnnualPlan,
  planTier,
  freeTrialEnabled,
  catchUpBkTreatment,
  selectedProductId,
  productPermissions,
}: {
  selectedAnnualPlan: boolean
  planTier?: PlanTier
  freeTrialEnabled?: boolean
  catchUpBkTreatment: CatchUpBkTreatment
  selectedProductId?: string
  productPermissions?: Record<string, ProductFeatureId[]>
}) => {
  const isScorp = useReselector(selectIsCurrentUserScorp)
  const isMobile = useIsDeviceWidth(DeviceWidth.mobile)
  const taxPackageLaunchFlag = useFlag(
    FEATURE_FLAG_KEYS.taxPackage2026Launch,
    false
  )
  const bankingBonusFlag = useFlag(FEATURE_FLAG_KEYS.signupBankingBonus, {
    enabled: false,
  })
  const bankingLine = resolveBankingBonusLine(bankingBonusFlag.value)
  const hasPayrollEntitlement = Boolean(
    selectedProductId &&
    productPermissions?.[selectedProductId]?.includes(ProductFeatureId.payroll)
  )
  const hasAnnualTaxesEntitlement = Boolean(
    selectedProductId &&
    productPermissions?.[selectedProductId]?.includes(
      ProductFeatureId.annualTaxes
    )
  )
  const stateFilingsLine = hasAnnualTaxesEntitlement
    ? UNLIMITED_STATE_FILINGS_COPY.promo
    : null
  const isWaived = catchUpBkTreatment === CatchUpBkTreatment.waived
  const catchupBookkeepingPrice = useCatchupBookkeepingPrice()
  const currentTaxYear = useReselector(selectCurrentAnnualTaxYear)
  const productDescription = useReselector(selectProductDescription, {
    sections: ['catch_up_bookkeeping'],
  })
  const couponProductId =
    taxPackageLaunchFlag.value && selectedProductId
      ? selectedProductId
      : undefined
  const { coupon: catchUpBKCoupon, isLoading: isLoadingLatestCoupon } =
    useLatestCatchUpBkCoupon(
      selectedAnnualPlan,
      couponProductId,
      !taxPackageLaunchFlag.value
    )
  const isLite = planTier === PlanTier.lite
  const showPackageSkeleton =
    taxPackageLaunchFlag.value && isLoadingLatestCoupon && !isLite
  const hasActiveTaxPackage = Boolean(catchUpBKCoupon?.taxPackage) && !isLite
  const taxFiling = useReselector(
    getAnnualTaxFilingForYearSelector,
    currentTaxYear
  )
  const approximatePracticeStartDate = useReselector(
    selectApproximatePracticeStartDate
  )
  const isLoadingCoupon = useReselector(selectIsFetchingForKeys, [
    FETCH_CATCH_UP_BK_COUPON(selectedAnnualPlan, couponProductId),
  ])
  const hasPromo = Boolean(catchUpBKCoupon?.percent_off)

  const currentYear = DateTime.now().year
  const lastYear = currentYear - 1

  const catchUpBkMonths = useFetchResponse(fetchCatchUpBKMonths)
  const monthsSoFar = catchUpBkMonths?.monthsThisYear || 0

  const canOptIntoPreviousTaxYear = useReselector(
    selectCanOptIntoPreviousTaxYear
  )

  const includePriorYearTaxes =
    Boolean(taxFiling?.isLateJoiner) &&
    Number(currentTaxYear) < currentYear &&
    canOptIntoPreviousTaxYear

  const monthsLastYear =
    catchUpBkMonths?.monthsLastYear || (includePriorYearTaxes ? 12 : 0)

  const additionalTooltipTextNoTaxes = useMemo(() => {
    if (approximatePracticeStartDate?.year === currentYear) {
      return `Since you started your practice in ${approximatePracticeStartDate.toFormat(DATE_FORMATS_LUXON.MONTH_LONG)} of this year, we need to perform Catch Up Bookkeeping for ${catchupBookkeepingPrice?.monthCount} ${catchupBookkeepingPrice?.monthCount === 1 ? 'month' : 'months'}.`
    }
    return `Since you started your practice ${approximatePracticeStartDate?.year ? `in ${approximatePracticeStartDate.year}` : 'before this year'}, we need to perform Catch Up Bookkeeping for all of ${currentYear}.`
  }, [
    approximatePracticeStartDate,
    currentYear,
    catchupBookkeepingPrice?.monthCount,
  ])

  const payrollLineCopy = useMemo(() => {
    if (taxPackageLaunchFlag.value) {
      if (!hasPayrollEntitlement) {
        return null
      }
      return formatPayrollPackageLine(
        catchUpBKCoupon?.taxPackage?.payroll ?? null
      )
    }

    if (planTier !== PlanTier.premium || !isScorp) {
      return null
    }

    const monthsOfFreePayroll = selectedAnnualPlan ? 12 : 6
    return `${monthsOfFreePayroll} free months of payroll (${formatCurrency(
      currency(45).multiply(monthsOfFreePayroll),
      { hideDecimalsIfZero: true }
    )} value)`
  }, [
    taxPackageLaunchFlag.value,
    hasPayrollEntitlement,
    catchUpBKCoupon,
    planTier,
    isScorp,
    selectedAnnualPlan,
  ])

  const advisoryLineCopy = useMemo(() => {
    if (!hasAnnualTaxesEntitlement) {
      return null
    }
    if (!taxPackageLaunchFlag.value) {
      return 'Complimentary 30 min tax consult ($150 value)'
    }

    return formatTaxAdvisoryPackageLine(
      catchUpBKCoupon?.taxPackage?.taxAdvisory ?? null
    )
  }, [hasAnnualTaxesEntitlement, taxPackageLaunchFlag.value, catchUpBKCoupon])

  const additionalTooltipTextWithTaxes = useMemo(() => {
    if (approximatePracticeStartDate?.year === Number(currentTaxYear)) {
      return `Since you started your practice in ${approximatePracticeStartDate.year}, and are also having us help you with your taxes for ${currentTaxYear}, we need to perform Catch Up Bookkeeping for all of ${currentYear}, and the months in ${currentTaxYear} starting with ${approximatePracticeStartDate.toFormat(DATE_FORMATS_LUXON.MONTH_LONG)}.`
    }
    return `Since you started your practice ${approximatePracticeStartDate?.year ? `in ${approximatePracticeStartDate.year}` : `before ${currentTaxYear}`}, and are also having us help you with your taxes for ${currentTaxYear}, we need to perform Catch Up Bookkeeping for all of ${currentTaxYear} and all of ${currentYear}.`
  }, [approximatePracticeStartDate, currentYear, currentTaxYear])

  const formattedAmountWithDiscount = useMemo(() => {
    if (!catchupBookkeepingPrice) {
      return null
    }
    return formatCurrency(
      discountedCatchUpBkAmount(
        catchupBookkeepingPrice.amount,
        catchUpBKCoupon?.percent_off
      ),
      {
        hideDecimalsIfZero: true,
      }
    )
  }, [catchupBookkeepingPrice, catchUpBKCoupon])

  if (
    !catchupBookkeepingPrice ||
    (!includePriorYearTaxes && monthsSoFar === 0)
  ) {
    if (showPackageSkeleton) {
      return <TaxPackageCardSkeleton />
    }
    if (taxPackageLaunchFlag.value && hasActiveTaxPackage) {
      return (
        <div>
          <TaxSeasonPackageCard
            cubkDiscount={null}
            payrollLine={payrollLineCopy}
            stateFilingsLine={stateFilingsLine}
            advisoryLine={advisoryLineCopy}
            bankingLine={bankingLine}
            freeTrialEnabled={freeTrialEnabled}
          />
        </div>
      )
    }
    return null
  }

  const percentOff = catchUpBKCoupon?.percent_off ?? 0
  const showCubkDiscount = hasPromo && !(taxPackageLaunchFlag.value && isWaived)
  const cubkDiscount: CubkDiscount | null = showCubkDiscount
    ? {
        percentOff,
        amount: formatCurrency(
          currency(catchupBookkeepingPrice.amount)
            .multiply(percentOff / 100)
            .multiply(-1),
          { hideDecimalsIfZero: true }
        ),
        savings: formatCurrency(
          currency(catchupBookkeepingPrice.amount).multiply(percentOff / 100),
          { hideDecimalsIfZero: true }
        ),
      }
    : null

  const showPromoCard =
    !isLite &&
    (taxPackageLaunchFlag.value
      ? hasActiveTaxPackage
      : hasPromo && !freeTrialEnabled)

  const pricePerMonth = currency(catchupBookkeepingPrice.costPerMonth)
  const costForThisYear = pricePerMonth.multiply(monthsSoFar)

  return (
    <div>
      <div className="flex justify-between items-center">
        <Text
          variant={isMobile ? TypographyStyles.H2 : TypographyStyles.DISPLAY_SM}
        >
          Catch Up Bookkeeping
        </Text>

        {isLoadingCoupon ? (
          <Loader active={isLoadingCoupon} />
        ) : (
          <div className="flex flex-row justify-end gap-3 items-center">
            {(isWaived || catchUpBKCoupon?.percent_off) && (
              <Badge color="green" variant="secondary">
                {isWaived || catchUpBKCoupon?.percent_off === 100
                  ? 'FREE'
                  : `${catchUpBKCoupon?.percent_off}% off`}
              </Badge>
            )}

            <Text
              variant={
                isMobile ? TypographyStyles.H2 : TypographyStyles.DISPLAY_SM
              }
              className={isWaived ? 'inline line-through' : 'inline'}
              color={isWaived ? TextColors.MUTED : undefined}
            >
              {formattedAmountWithDiscount}
            </Text>
          </div>
        )}
      </div>

      {catchUpBkTreatment !== CatchUpBkTreatment.chargeAtSignup && (
        <Text
          variant={TypographyStyles.BODY_SM}
          color={TextColors.MUTED}
          className="mt-1"
        >
          {isWaived
            ? 'Included with your free trial'
            : 'Billed after your free trial ends'}
        </Text>
      )}

      {monthsSoFar !== 0 && (
        <div className="flex justify-between items-center mt-1">
          <Tooltip>
            <Tooltip.Trigger>
              <Text
                variant={TypographyStyles.BODY_SM}
                className="border-b-2 border-green-50 border-dotted"
              >
                {monthsSoFar} month{monthsSoFar !== 1 && 's'} at{' '}
                {formatCurrency(pricePerMonth, {
                  hideDecimalsIfZero: true,
                })}
                /mo for {currentYear} taxes
              </Text>
            </Tooltip.Trigger>

            <Tooltip.Content>
              <Text>
                In order to ensure that we have the most accurate picture of
                your finances, a Heard bookkeeper will review, categorize and
                validate all your previous transactions for accuracy.
              </Text>

              <Text>{additionalTooltipTextNoTaxes}</Text>
            </Tooltip.Content>
          </Tooltip>
          <div className="flex flex-col gap-0.5 items-end">
            <Text
              className={hasPromo ? 'line-through' : undefined}
              color={hasPromo ? TextColors.MUTED : undefined}
              textAlign="right"
            >
              {formatCurrency(costForThisYear, {
                hideDecimalsIfZero: true,
              })}
            </Text>
            <Text
              color={TextColors.MUTED}
              variant={TypographyStyles.BODY_XS}
              textAlign="right"
            >
              One time fee
            </Text>
          </div>
        </div>
      )}

      {includePriorYearTaxes && (
        <div className="flex justify-between items-center mt-1">
          <Tooltip>
            <Tooltip.Trigger>
              <Text
                variant={TypographyStyles.BODY_SM}
                className="border-b-2 border-green-50 border-dotted"
              >
                {monthsLastYear} months at{' '}
                {formatCurrency(pricePerMonth, {
                  hideDecimalsIfZero: true,
                })}
                /mo of catch up bookkeeping for {lastYear} taxes
              </Text>
            </Tooltip.Trigger>

            <Tooltip.Content>
              <Text>
                The first step in preparing your {lastYear} taxes is a review of
                your books. A Heard bookkeeper will review, categorize and
                validate all your {lastYear} transactions for accuracy. This
                ensures that Heard finds all of the deductions that you qualify
                for.
              </Text>

              <Text>{additionalTooltipTextWithTaxes}</Text>
            </Tooltip.Content>
          </Tooltip>

          <div className="flex flex-col gap-0.5 items-end">
            <Text
              className={hasPromo ? 'line-through' : undefined}
              color={hasPromo ? TextColors.MUTED : undefined}
              textAlign="right"
            >
              {formatCurrency(pricePerMonth.multiply(monthsLastYear), {
                hideDecimalsIfZero: true,
              })}
            </Text>
            <Text
              color={TextColors.MUTED}
              variant={TypographyStyles.BODY_XS}
              textAlign="right"
            >
              One time fee
            </Text>
          </div>
        </div>
      )}

      {showPromoCard && !taxPackageLaunchFlag.value ? (
        <TaxPackageCard
          cubkDiscount={cubkDiscount}
          payrollLine={payrollLineCopy}
          stateFilingsLine={stateFilingsLine}
          advisoryLine={advisoryLineCopy}
        />
      ) : null}

      <DetailsAccordion
        productDescription={productDescription}
        hideTitle
        forOneTimePayment
      />

      {showPackageSkeleton ? <TaxPackageCardSkeleton /> : null}
      {!showPackageSkeleton && showPromoCard && taxPackageLaunchFlag.value ? (
        <TaxSeasonPackageCard
          cubkDiscount={cubkDiscount}
          payrollLine={payrollLineCopy}
          stateFilingsLine={stateFilingsLine}
          advisoryLine={advisoryLineCopy}
          bankingLine={bankingLine}
          freeTrialEnabled={freeTrialEnabled}
        />
      ) : null}
    </div>
  )
}
